import type { SupportInboxPermission } from '../generated/graphql';

/**
 * The four Support Inbox permissions in plain words (SKU-1775).
 *
 * ONE copy, read by the role editor's permission picker and by the "who can use the Inbox"
 * summary on the inbox settings page, so an admin granting INBOX:REPLY reads the same sentence
 * in both places. Mirrors the split `permissionService.ts` seeds and `builder.ts` enforces;
 * an exhaustive `Record` over the generated enum makes a fifth permission a compile error here.
 */
export const INBOX_PERMISSION_INFO: Record<
  SupportInboxPermission,
  { label: string; description: string }
> = {
  READ: {
    label: 'Read Inbox',
    description:
      'Open the Inbox and read conversations and internal notes. This is what makes the Inbox appear in the sidebar.',
  },
  REPLY: {
    label: 'Reply in Inbox',
    description: "Email customers in the organization's name and write internal notes.",
  },
  MANAGE: {
    label: 'Manage Inbox',
    description:
      'Triage conversations — assign, change status, snooze and tag — and manage saved replies (macros).',
  },
  ADMIN: {
    label: 'Administer Inbox',
    description: 'Configure inboxes, their addresses and forwarding.',
  },
};

export const INBOX_PERMISSION_ORDER: readonly SupportInboxPermission[] = [
  'READ',
  'REPLY',
  'MANAGE',
  'ADMIN',
];

/** `INBOX:<action>` from a permission row, or null when the row is not an inbox permission. */
export function inboxPermissionOf(resource: string, action: string): SupportInboxPermission | null {
  if (resource.toUpperCase() !== 'INBOX') return null;
  const upper = action.toUpperCase();
  return INBOX_PERMISSION_ORDER.find((permission) => permission === upper) ?? null;
}
